import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Grid, HeartPulse, Image as ImageIcon, List, Loader2, RefreshCw, Trash2, UploadCloud, Crosshair, HardDrive, DownloadCloud } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const CONCURRENCY = 4;
const LS_KEY = "siyaara_media_sessions";

const loadSessions = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; } };
const saveSessions = (s) => localStorage.setItem(LS_KEY, JSON.stringify(s));

export default function MediaLibrary() {
  const [tab, setTab] = useState("upload");
  const [cfg, setCfg] = useState(null);
  const [queue, setQueue] = useState([]); // {id, file, name, size, status, progress, error, asset_id, match}
  const [matchPreview, setMatchPreview] = useState(null);
  const [assets, setAssets] = useState([]);
  const [health, setHealth] = useState(null);
  const [view, setView] = useState("grid");
  const [filter, setFilter] = useState({ q: "", kind: "", tag: "" });
  const [sel, setSel] = useState({});
  const [bulkTags, setBulkTags] = useState({ model: "", category: "", shot_type: "", campaign: "" });
  const [assignProduct, setAssignProduct] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [storageTest, setStorageTest] = useState(null);
  const [migration, setMigration] = useState(null);
  const [budget, setBudget] = useState(null);
  const [ratesDraft, setRatesDraft] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [purgeSel, setPurgeSel] = useState({});
  const [pw, setPw] = useState(null);
  const [pwSlug, setPwSlug] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [focalAsset, setFocalAsset] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [importSel, setImportSel] = useState({});
  const [assignments, setAssignments] = useState({});
  const [importJob, setImportJob] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const runningRef = useRef(0);

  useEffect(() => {
    api.get("/admin/products", { params: { limit: 200 } }).then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.items || r.data?.products || [];
      setAllProducts(list);
    }).catch(() => {});
  }, []);

  useEffect(() => { api.get("/admin/media/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const loadAssets = useCallback(() => {
    const params = new URLSearchParams();
    if (filter.q) params.set("q", filter.q);
    if (filter.kind) params.set("kind", filter.kind);
    if (filter.tag) params.set("tag", filter.tag);
    api.get(`/admin/media/assets?${params}`).then((r) => setAssets(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { if (tab === "library") loadAssets(); }, [tab, loadAssets]);
  useEffect(() => { if (tab === "health") api.get("/admin/media/health").then((r) => setHealth(r.data)).catch(() => {}); }, [tab]);

  const refreshBudget = useCallback(() => {
    api.get("/admin/media/storage/overview").then((r) => { setBudget(r.data); setRatesDraft(r.data.rates); }).catch(() => {});
    api.get("/admin/media/storage/orphans").then((r) => setOrphans(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (tab === "storage") refreshBudget(); }, [tab, refreshBudget]);

  const saveRates = async () => {
    try {
      await api.put("/admin/settings", { storage_budget: ratesDraft });
      toast.success("Rates saved");
      refreshBudget();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const purgeOrphans = async () => {
    const keys = Object.keys(purgeSel).filter((k) => purgeSel[k]);
    if (!keys.length) return;
    if (!window.confirm(`Purge ${keys.length} orphaned deriv keys? This cannot be undone.`)) return;
    try {
      const r = await api.post("/admin/media/storage/purge-orphans", { keys });
      toast.success(`Purged ${r.data.deleted} keys — freed ${r.data.freed_mb} MB`);
      setPurgeSel({});
      refreshBudget();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runPageWeight = async () => {
    setPwBusy(true);
    try {
      const r = await api.get("/admin/media/page-weight", { params: pwSlug ? { slug: pwSlug } : { sample: 8 } });
      setPw(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setPwBusy(false); }
  };

  useEffect(() => {
    if (assignProduct.length > 1) {
      api.get(`/admin/products?q=${encodeURIComponent(assignProduct)}`).then((r) => {
        const items = Array.isArray(r.data) ? r.data : r.data.products || r.data.items || [];
        setProductOptions(items.slice(0, 8));
      }).catch(() => {});
    }
  }, [assignProduct]);

  const onDrop = async (ev) => {
    ev.preventDefault();
    const files = [...ev.dataTransfer.files];
    addFiles(files);
  };

  const addFiles = async (files) => {
    const sessions = loadSessions();
    const items = files.map((f) => {
      const prior = sessions[`${f.name}:${f.size}`];
      return {
        id: Math.random().toString(36).slice(2), file: f, name: f.name, size: f.size,
        status: "queued", progress: 0, error: "", asset_id: "",
        resume: prior || null,
      };
    });
    setQueue((q) => [...q, ...items]);
    const mp = await api.post("/admin/media/match-preview", { filenames: files.map((f) => f.name) }).catch(() => null);
    if (mp) setMatchPreview(mp.data);
  };

  const updateItem = (id, patch) => setQueue((q) => q.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const doChunked = async (uploadId, chunkSize, startChunk, item) => {
    const total = Math.ceil(item.size / chunkSize);
    for (let n = startChunk; n < total; n++) {
      const blob = item.file.slice(n * chunkSize, (n + 1) * chunkSize);
      await api.put(`/admin/media/upload/${uploadId}/chunk/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
      updateItem(item.id, { progress: Math.round(((n + 1) / total) * 100) });
    }
  };

  const uploadOne = async (item) => {
    updateItem(item.id, { status: "uploading", error: "" });
    try {
      let uploadId = item.resume?.upload_id;
      let startChunk = 0;
      if (uploadId) {
        try {
          const st = await api.get(`/admin/media/upload/${uploadId}`);
          startChunk = Math.max(...(st.data.chunks || [-1])) + 1;
        } catch { uploadId = null; }
      }
      let chunkSize = cfg?.chunk_size || 8 * 1024 * 1024;
      let presigned = null;
      if (!uploadId) {
        const init = await api.post("/admin/media/upload/init", {
          filename: item.name, size: item.size, content_type: item.file.type, tags: {},
        });
        uploadId = init.data.upload_id;
        chunkSize = init.data.chunk_size;
        presigned = init.data.presigned_url;
        const sessions = loadSessions();
        sessions[`${item.name}:${item.size}`] = { upload_id: uploadId };
        saveSessions(sessions);
      }
      if (presigned) {
        // Direct browser→R2 PUT. If the bucket CORS policy blocks it (or the
        // network fails) the request errors or returns non-2xx — fall back to a
        // server-side chunked upload, which has no browser-CORS dependency.
        let ok = false;
        try {
          const res = await fetch(presigned, { method: "PUT", body: item.file,
            headers: item.file.type ? { "Content-Type": item.file.type } : {} });
          ok = res.ok;
          if (!ok) console.warn(`presigned PUT ${res.status} — falling back to chunked`);
        } catch (err) {
          console.warn("presigned PUT blocked (CORS/network) — falling back to chunked", err);
        }
        if (!ok) {
          const reinit = await api.post("/admin/media/upload/init", {
            filename: item.name, size: item.size, content_type: item.file.type,
            tags: {}, force_chunked: true,
          });
          uploadId = reinit.data.upload_id;
          chunkSize = reinit.data.chunk_size;
          const sessions = loadSessions();
          sessions[`${item.name}:${item.size}`] = { upload_id: uploadId };
          saveSessions(sessions);
          await doChunked(uploadId, chunkSize, 0, item);
        }
      } else {
        await doChunked(uploadId, chunkSize, startChunk, item);
      }
      updateItem(item.id, { status: "processing" });
      await api.post(`/admin/media/upload/${uploadId}/complete`);
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const st = await api.get(`/admin/media/upload/${uploadId}`);
        if (st.data.status === "done") {
          updateItem(item.id, { status: "done", asset_id: st.data.asset_id });
          const sessions = loadSessions();
          delete sessions[`${item.name}:${item.size}`];
          saveSessions(sessions);
          return st.data.asset_id;
        }
        if (st.data.status === "failed") throw new Error(st.data.error || "processing failed");
      }
      throw new Error("processing timed out");
    } catch (e) {
      updateItem(item.id, { status: "failed", error: errMsg(e) });
      return null;
    }
  };

  const runQueue = async () => {
    const pending = queue.filter((i) => ["queued", "failed"].includes(i.status));
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (true) {
        const next = pending.shift();
        if (!next) break;
        runningRef.current++;
        await uploadOne(next);
        runningRef.current--;
      }
    });
    await Promise.all(workers);
    toast.success("Batch finished");
  };

  const autoAssign = async () => {
    const doneAssets = queue.filter((i) => i.status === "done" && i.asset_id);
    const matched = matchPreview?.matched || [];
    const items = [];
    for (const m of matched) {
      const q = doneAssets.find((a) => a.name === m.filename);
      if (q) items.push({ asset_id: q.asset_id, product_id: m.product_id, order: m.order });
    }
    if (!items.length) return toast.error("No matched uploads to assign yet");
    const r = await api.post("/admin/media/assets/auto-assign", { items });
    toast.success(`Assigned images to ${r.data.products_updated} products`);
    setMatchPreview(null);
  };

  const manualAssign = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length || !productOptions.length) return toast.error("Select assets and search a product");
    const pid = productOptions[0].id || productOptions[0]._id;
    await api.post("/admin/media/assets/assign", { product_id: pid, assets: ids.map((id) => ({ asset_id: id, order: 99 })) });
    toast.success(`Attached ${ids.length} assets to ${productOptions[0].title}`);
    setSel({});
    loadAssets();
  };

  const applyTags = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) return toast.error("Select assets first");
    await api.post("/admin/media/assets/tags", { asset_ids: ids, tags: bulkTags });
    toast.success("Tags applied");
    loadAssets();
  };

  const bulkDelete = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) return;
    const usage = await api.get(`/admin/media/usage?ids=${ids.join(",")}`);
    const n = usage.data.products_affected_count;
    if (!window.confirm(`Delete ${ids.length} assets? ${n} product(s) currently use them and would lose images: ${usage.data.products_affected.slice(0, 5).join(", ")}${n > 5 ? "…" : ""}`)) return;
    const r = await api.post("/admin/media/assets/delete", { asset_ids: ids });
    toast.success(`Deleted ${r.data.deleted} assets`);
    setSel({});
    loadAssets();
  };

  const selAll = () => {
    const all = {};
    assets.forEach((a) => { all[a.id] = true; });
    setSel(all);
  };

  const testStorage = async () => {
    setStorageTest({ testing: true });
    try {
      const r = await api.post("/admin/media/storage/test");
      setStorageTest(r.data);
    } catch (e) { setStorageTest({ ok: false, error: errMsg(e) }); }
  };

  const startMigration = async () => {
    if (!window.confirm("Copy all local media to the configured bucket, verify each object, then rewrite stored URLs? Local files are kept (re-run safe).")) return;
    try {
      await api.post("/admin/media/storage/migrate", {});
      pollMigration();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pollMigration = async () => {
    for (let i = 0; i < 150; i++) {
      try {
        const r = await api.get("/admin/media/storage/migrate/status");
        setMigration(r.data);
        if (r.data.status && r.data.status !== "running") return;
      } catch { /* keep polling */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  const saveFocal = async (asset, x, y) => {
    try {
      await api.patch(`/admin/media/assets/${asset.id}/focal`, { x, y });
      setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, focal: { x, y } } : a)));
      setFocalAsset((f) => (f ? { ...f, focal: { x, y } } : f));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const skuExamples = allProducts.map((p) => p.variants?.[0]?.sku || p.slug || p.title).filter(Boolean).slice(0, 2);

  const scanBucket = async () => {
    setScanBusy(true);
    try {
      const r = await api.get("/admin/media/bucket/scan");
      setScan(r.data);
      setImportSel({});
    } catch (e) { toast.error(errMsg(e)); setScan(null); }
    finally { setScanBusy(false); }
  };

  const pollImportJob = async () => {
    for (let i = 0; i < 300; i++) {
      try {
        const r = await api.get("/admin/media/bucket/adopt/status");
        setImportJob(r.data);
        if (r.data.status && r.data.status !== "running") { scanBucket(); return; }
      } catch { /* keep polling */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  const adopt = async (keys) => {
    try {
      const r = await api.post("/admin/media/bucket/adopt",
        keys?.length ? { keys, assignments } : { all: true, assignments });
      if (r.data.status === "nothing_to_adopt") { toast.info("Nothing to adopt"); return; }
      toast.success(`Import started for ${r.data.total} files`);
      setAssignments({});
      pollImportJob();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="media-library-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Media Library</h1>
          <p className="text-sm text-neutral-500">
            Driver: <b>{cfg?.driver || "…"}</b> · originals archived, storefront serves derivatives only.
          </p>
        </div>
        <div className="flex gap-2">
          {[["upload", UploadCloud, "Upload"], ["import", DownloadCloud, "Import from Bucket"], ["unmatched", Grid, "Unmatched"], ["library", ImageIcon, "Library"], ["storage", HardDrive, "Storage"], ["health", HeartPulse, "Health"]].map(([k, Icon, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-xs rounded flex items-center gap-1.5 ${tab === k ? "bg-neutral-900 text-white" : "border"}`} data-testid={`media-tab-${k}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-brand-light/40 border border-brand/15 rounded-lg p-3 text-xs text-neutral-700" data-testid="media-route-note">
        <b>Recommended route:</b> upload through this screen — it handles resizing, derivatives and product matching
        automatically. Files placed directly into the bucket (R2 dashboard etc.) must be brought in via <b>Import from Bucket</b>.
        <details className="mt-1.5">
          <summary className="cursor-pointer text-brand font-medium" data-testid="naming-helper-toggle">Naming conventions — how files attach automatically</summary>
          <div className="mt-2 space-y-1 font-mono text-[11px] text-neutral-600" data-testid="naming-helper">
            <p>· <b>SKU.jpg</b> — attaches to the product with that SKU{skuExamples[0] ? ` (e.g. ${skuExamples[0]}.jpg)` : ""}</p>
            <p>· <b>SKU-2.jpg</b> or <b>SKU_02.jpg</b> — extra images of that product; the number sets image order, lowest becomes primary{skuExamples[0] ? ` (e.g. ${skuExamples[0]}-2.jpg)` : ""}</p>
            <p>· <b>SKU/any-name.jpg</b> — a folder named after a SKU or handle attaches everything inside it{skuExamples[1] ? ` (e.g. ${skuExamples[1]}/front.jpg)` : ""}</p>
            <p>· Product handles work the same way (handle-1.jpg). Unsupported file types are skipped and reported — they never fail the run.</p>
          </div>
        </details>
      </div>

      {tab === "upload" && (
        <>
          <section onDrop={onDrop} onDragOver={(e) => e.preventDefault()}
            className="bg-white border-2 border-dashed rounded-lg p-10 text-center space-y-2 hover:border-brand transition-colors"
            data-testid="media-dropzone">
            <UploadCloud size={32} className="mx-auto text-neutral-400" />
            <p className="text-sm font-medium">Drop images / videos here — or a whole folder</p>
            <p className="text-xs text-neutral-400">JPG · PNG · WebP · HEIC · MP4 · MOV — images ≤ {cfg?.max_image_mb ?? 50} MB, video ≤ {cfg?.max_video_mb ?? 500} MB. Chunked + resumable: closing the tab is safe, re-drop the same files to resume.</p>
            <label className="inline-block px-4 py-2 bg-neutral-900 text-white text-xs rounded cursor-pointer" data-testid="browse-files-btn">
              Browse files
              <input type="file" multiple className="hidden" accept=".jpg,.jpeg,.png,.webp,.heic,.mp4,.mov"
                onChange={(e) => addFiles([...e.target.files])} />
            </label>
            <label className="inline-block px-4 py-2 border text-xs rounded cursor-pointer ml-2" data-testid="browse-folder-btn">
              Browse folder
              <input type="file" className="hidden" {...{ webkitdirectory: "" }} onChange={(e) => addFiles([...e.target.files])} />
            </label>
          </section>

          {matchPreview && (
            <section className="bg-white border rounded p-4 text-xs space-y-2" data-testid="match-preview">
              <b>Auto-matching:</b> <span className="text-green-700">{matchPreview.matched.length} files matched to products</span> · <span className="text-amber-700">{matchPreview.unmatched.length} unmatched</span> (assign manually after upload)
              <div className="max-h-32 overflow-y-auto grid md:grid-cols-2 gap-x-6">
                {matchPreview.matched.map((m) => <div key={m.filename} className="py-0.5">✓ {m.filename} → <b>{m.product_title}</b> (order {m.order})</div>)}
                {matchPreview.unmatched.map((u) => <div key={u.filename} className="py-0.5 text-neutral-400">✗ {u.filename}</div>)}
              </div>
            </section>
          )}

          {queue.length > 0 && (
            <section className="bg-white border rounded p-4 space-y-2" data-testid="upload-queue">
              <div className="flex items-center justify-between">
                <b className="text-sm">{queue.filter((i) => i.status === "done").length}/{queue.length} complete</b>
                <div className="flex gap-2">
                  <button onClick={runQueue} className="px-4 py-2 bg-brand text-white text-xs rounded font-medium" data-testid="start-upload-btn">Start upload</button>
                  <button onClick={() => { setQueue(queue.filter((i) => i.status !== "failed")); }} className="px-3 py-2 border text-xs rounded" data-testid="clear-failed-btn">Clear failed</button>
                  <button onClick={autoAssign} className="px-3 py-2 border text-xs rounded" data-testid="auto-assign-btn">Auto-assign matched</button>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y text-xs">
                {queue.map((i) => (
                  <div key={i.id} className="py-2 flex items-center gap-3" data-testid={`queue-${i.name}`}>
                    {/\.(mp4|mov)$/i.test(i.name) ? <Film size={14} /> : <ImageIcon size={14} />}
                    <span className="w-56 truncate">{i.name}</span>
                    <div className="flex-1 h-1.5 bg-neutral-100 rounded overflow-hidden">
                      <div className={`h-full transition-all ${i.status === "failed" ? "bg-red-500" : "bg-brand"}`} style={{ width: `${i.status === "done" ? 100 : i.progress}%` }} />
                    </div>
                    <span className={`w-24 text-right ${i.status === "failed" ? "text-red-600" : i.status === "done" ? "text-green-700" : "text-neutral-500"}`}>
                      {i.status === "uploading" ? `${i.progress}%` : i.status}{i.resume && i.status === "queued" ? " (resume)" : ""}
                    </span>
                    {i.status === "failed" && <button title={i.error} onClick={() => updateItem(i.id, { status: "queued" })} className="text-brand"><RefreshCw size={12} /></button>}
                  </div>
                ))}
              </div>
              <button onClick={() => setQueue(queue.map((i) => (i.status === "failed" ? { ...i, status: "queued" } : i)))} className="text-xs text-brand hover:underline" data-testid="retry-all-failed-btn">Retry all failed</button>
            </section>
          )}
        </>
      )}

      {tab === "unmatched" && <UnmatchedTab onDone={loadAssets} />}

      {tab === "library" && (
        <>
          <section className="bg-white border rounded p-3 flex flex-wrap items-center gap-2 text-xs" data-testid="library-filters">
            <input placeholder="Search name…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="border rounded px-2 py-1.5" data-testid="filter-q" />
            <select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })} className="border rounded px-2 py-1.5" data-testid="filter-kind">
              <option value="">All types</option><option value="image">Images</option><option value="video">Videos</option>
            </select>
            <input placeholder="Tag…" value={filter.tag} onChange={(e) => setFilter({ ...filter, tag: e.target.value })} className="border rounded px-2 py-1.5 w-28" data-testid="filter-tag" />
            <button onClick={loadAssets} className="px-3 py-1.5 border rounded">Apply</button>
            <div className="h-6 w-px bg-neutral-200" />
            <button onClick={() => setView(view === "grid" ? "list" : "grid")} className="px-2 py-1.5 border rounded" data-testid="view-toggle">{view === "grid" ? <List size={13} /> : <Grid size={13} />}</button>
            <button onClick={selAll} className="px-2 py-1.5 border rounded" data-testid="select-all-btn">Select all filtered</button>
            <button onClick={bulkDelete} className="px-2 py-1.5 border border-red-200 text-red-600 rounded flex items-center gap-1" data-testid="bulk-delete-btn"><Trash2 size={12} /> Delete selected</button>
            <div className="h-6 w-px bg-neutral-200" />
            {["model", "category", "shot_type", "campaign"].map((k) => (
              <input key={k} placeholder={k.replace("_", " ")} value={bulkTags[k]} onChange={(e) => setBulkTags({ ...bulkTags, [k]: e.target.value })} className="border rounded px-2 py-1.5 w-24" data-testid={`tag-${k}`} />
            ))}
            <button onClick={applyTags} className="px-3 py-1.5 bg-neutral-900 text-white rounded" data-testid="apply-tags-btn">Tag selected</button>
            <div className="h-6 w-px bg-neutral-200" />
            <input placeholder="Assign to product…" value={assignProduct} onChange={(e) => setAssignProduct(e.target.value)} className="border rounded px-2 py-1.5 w-44" list="media-products" data-testid="assign-product-input" />
            <datalist id="media-products">{productOptions.map((p) => <option key={p.id || p._id} value={p.title} />)}</datalist>
            <button onClick={manualAssign} className="px-3 py-1.5 border rounded" data-testid="manual-assign-btn">Attach</button>
          </section>
          <section className={view === "grid" ? "grid grid-cols-3 md:grid-cols-6 gap-3" : "divide-y bg-white border rounded"} data-testid="asset-list">
            {assets.map((a) => (
              <div key={a.id} className={view === "grid" ? "bg-white border rounded overflow-hidden" : "flex items-center gap-3 p-2"}>
                <label className="flex items-start gap-2 p-2">
                  <input type="checkbox" checked={!!sel[a.id]} onChange={(e) => setSel({ ...sel, [a.id]: e.target.checked })} data-testid={`asset-${a.id}`} />
                  {a.kind === "video" ? (
                    <video src={a.url} poster={a.poster} muted preload="none" className={view === "grid" ? "w-full aspect-square object-cover" : "w-14 h-14 object-cover rounded"} />
                  ) : (
                    <img src={a.blur ? a.url : a.url} alt={a.name} loading="lazy"
                      srcSet={a.srcset ? Object.entries(a.srcset).map(([w, f]) => `${f.webp} ${w}w`).join(", ") : undefined}
                      sizes="(max-width: 768px) 33vw, 16vw"
                      className={view === "grid" ? "w-full aspect-square object-cover" : "w-14 h-14 object-cover rounded"} />
                  )}
                </label>
                <div className="px-2 pb-2 text-[11px] text-neutral-500 truncate">
                  {a.name}
                  <div className="text-neutral-400">{(a.bytes_derivatives / 1024).toFixed(0)} KB deriv · {a.products?.length || 0} products</div>
                  {a.kind === "image" && (
                    <button onClick={() => setFocalAsset(a)} className="mt-1 px-2 py-1 border rounded text-[10px] flex items-center gap-1 hover:border-brand" data-testid={`focal-edit-${a.id}`}>
                      <Crosshair size={11} /> Focal {a.focal ? `${Math.round(a.focal.x)},${Math.round(a.focal.y)}` : "auto"}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!assets.length && <div className="col-span-full p-8 text-center text-neutral-400 text-sm">No media yet — upload something.</div>}
          </section>
        </>
      )}

      {tab === "import" && (
        <section className="space-y-4" data-testid="import-tab">
          <div className="bg-white border rounded p-4">
            <b className="text-sm">Import from Bucket</b>
            <p className="text-xs text-neutral-500 mt-1">
              Adopts photography placed directly into the bucket: creates the media record, generates the full derivative
              set (WebP + AVIF at 320/640/960/1440/2000, blur placeholder), strips EXIF, archives the original under the
              private <code>archive/</code> prefix, and attaches to products by SKU/handle filename or folder name.
            </p>
            <button onClick={scanBucket} disabled={scanBusy}
              className="mt-3 px-4 py-2 bg-neutral-900 text-white text-xs rounded disabled:opacity-50" data-testid="bucket-scan-btn">
              {scanBusy ? "Scanning…" : "Scan bucket"}
            </button>
            {scan && (
              <p className="text-xs mt-2" data-testid="bucket-scan-summary">
                <b>{scan.count}</b> unregistered files · {(scan.total_bytes / 1048576).toFixed(1)} MB
                · {scan.matched} auto-matched · {scan.unmatched} unmatched · {scan.unsupported} unsupported (will be skipped)
              </p>
            )}
          </div>

          {importJob?.status === "running" && (
            <div className="bg-white border rounded p-4" data-testid="import-progress">
              <p className="text-xs">Importing… {importJob.done || 0}/{importJob.total || 0}</p>
              <div className="h-1.5 bg-neutral-100 rounded mt-2">
                <div className="h-full bg-brand rounded transition-all" style={{ width: `${(100 * (importJob.done || 0)) / (importJob.total || 1)}%` }} />
              </div>
            </div>
          )}
          {importJob?.status === "done" && (
            <div className="bg-white border rounded p-4" data-testid="import-done">
              <b className="text-sm">Last import</b>
              <p className="text-xs mt-1" data-testid="import-summary">{importJob.summary}</p>
              <div className="max-h-48 overflow-y-auto mt-2 divide-y divide-neutral-100">
                {(importJob.results || []).map((r, i) => (
                  <p key={i} className={`text-[11px] py-1 ${r.status === "failed" ? "text-red-600" : r.status === "skipped" ? "text-amber-600" : "text-emerald-700"}`} data-testid={`import-result-${i}`}>
                    {r.key} — {r.status}{r.reason ? ` (${r.reason})` : ""}{r.error ? `: ${r.error}` : ""}{r.product ? ` → ${r.product}` : ""}
                  </p>
                ))}
              </div>
            </div>
          )}

          {scan && scan.files.length > 0 && (
            <div className="bg-white border rounded p-4 space-y-3" data-testid="match-preview">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <b className="text-sm">Matching preview — nothing committed yet</b>
                <div className="flex gap-2">
                  <button onClick={() => adopt(Object.keys(importSel).filter((k) => importSel[k]))}
                    disabled={!Object.values(importSel).some(Boolean)}
                    className="px-3 py-1.5 border text-xs rounded disabled:opacity-40" data-testid="adopt-selected-btn">Adopt selected</button>
                  <button onClick={() => adopt(null)} className="px-3 py-1.5 bg-brand text-white text-xs rounded" data-testid="adopt-all-btn">
                    Adopt all ({scan.count})
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[760px]">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-neutral-500 border-b">
                      <th className="p-2 w-8">
                        <input type="checkbox" data-testid="import-select-all"
                          onChange={(e) => {
                            const all = {};
                            if (e.target.checked) scan.files.forEach((f) => { if (f.supported) all[f.key] = true; });
                            setImportSel(all);
                          }} />
                      </th>
                      <th className="p-2">Preview</th><th className="p-2">File</th><th className="p-2">Size</th>
                      <th className="p-2">Matches</th><th className="p-2">Position</th><th className="p-2">Assign manually</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scan.files.map((f) => (
                      <tr key={f.key} className="border-b border-neutral-100" data-testid={`bucket-file-${f.key.replace(/[^a-z0-9]/gi, "-")}`}>
                        <td className="p-2">
                          <input type="checkbox" disabled={!f.supported} checked={!!importSel[f.key]}
                            onChange={(e) => setImportSel((s) => ({ ...s, [f.key]: e.target.checked }))} />
                        </td>
                        <td className="p-2">{f.thumb ? <img src={f.thumb} alt="" className="h-10 w-10 object-cover rounded" /> : <span className="text-neutral-300">—</span>}</td>
                        <td className="p-2 font-mono break-all">
                          {f.key}
                          {!f.supported && <span className="text-amber-600 ml-1">(unsupported — will be skipped)</span>}
                        </td>
                        <td className="p-2 whitespace-nowrap">{(f.size / 1024).toFixed(0)} KB</td>
                        <td className="p-2">
                          {f.match
                            ? <span className="text-emerald-700">{f.match.product_title} <span className="text-neutral-400">({f.match.via})</span></span>
                            : <span className="text-neutral-400">—</span>}
                          {f.match?.slot === "spec" && <span className="block text-[10px] font-mono text-indigo-600">→ spec sheet slot</span>}
                          {f.match?.colour && <span className="block text-[10px] font-mono text-stone-500">→ {f.match.colour} {f.match.slot === "video" ? "video set" : "image set"}</span>}
                        </td>
                        <td className="p-2 font-mono">{f.match ? (f.match.order === 0 ? "primary" : `#${f.match.order}`) : "—"}</td>
                        <td className="p-2">
                          {!f.match && f.supported && (
                            <select value={assignments[f.key] || ""}
                              onChange={(e) => setAssignments((a) => ({ ...a, [f.key]: e.target.value }))}
                              className="h-7 border rounded text-[11px] max-w-44 bg-white"
                              data-testid={`assign-${f.key.replace(/[^a-z0-9]/gi, "-")}`}>
                              <option value="">Choose product…</option>
                              {allProducts.map((p) => <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>)}
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {scan && !scan.files.length && (
            <p className="text-xs text-neutral-400" data-testid="bucket-empty">Bucket is clean — every object already has a media record.</p>
          )}
        </section>
      )}

      {tab === "storage" && (
        <section className="space-y-4" data-testid="storage-tab">
          <div className="bg-white border rounded p-4">
            <b className="text-sm">Object storage (S3-compatible)</b>
            <p className="text-xs text-neutral-500 mt-1">
              Active driver: <b>{cfg?.driver}</b>. The switch is configuration-only: set MEDIA_DRIVER, MEDIA_BUCKET,
              MEDIA_ENDPOINT, MEDIA_ACCESS_KEY, MEDIA_SECRET_KEY and MEDIA_PUBLIC_BASE_URL, then test, then migrate.
              Originals live under an <code>archive/</code> prefix that is never served publicly.
            </p>
            <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-mono">
              {[["MEDIA_BUCKET", cfg?.storage?.bucket], ["MEDIA_ENDPOINT", cfg?.storage?.endpoint],
                ["KEYS", cfg?.storage?.keys], ["PUBLIC BASE URL", cfg?.storage?.public_base_url]].map(([k, ok]) => (
                <span key={k} className={`px-2 py-1 rounded border ${ok ? "border-emerald-300 text-emerald-700" : "border-neutral-200 text-neutral-400"}`} data-testid={`storage-env-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                  {k}: {ok ? "set" : "not set"}
                </span>
              ))}
              <span className="px-2 py-1 rounded border border-neutral-200 text-neutral-500" data-testid="storage-env-region">REGION: {cfg?.storage?.region || "auto"}</span>
            </div>
            <button onClick={testStorage} className="mt-3 px-4 py-2 bg-neutral-900 text-white text-xs rounded" data-testid="storage-test-btn">
              {storageTest?.testing ? "Testing…" : "Test connection"}
            </button>
            {storageTest && !storageTest.testing && (
              <div className={`mt-3 text-xs rounded border p-3 ${storageTest.ok ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-700"}`} data-testid="storage-test-result">
                {storageTest.ok
                  ? <>Connection OK — bucket <b>{storageTest.bucket}</b> · {storageTest.probe} · public base {storageTest.public_base_url}</>
                  : <>Connection failed: {storageTest.error}</>}
              </div>
            )}
          </div>

          <div className="bg-white border rounded p-4" data-testid="migration-panel">
            <b className="text-sm">Migrate local media → bucket</b>
            <p className="text-xs text-neutral-500 mt-1">
              Copies every local object, verifies each remote copy byte-for-byte, then rewrites stored URLs to the
              public base URL. Idempotent — re-runs skip what already migrated. Local files are kept until you confirm.
            </p>
            <button onClick={startMigration} disabled={migration?.status === "running"}
              className="mt-3 px-4 py-2 bg-brand text-white text-xs rounded disabled:opacity-50" data-testid="migrate-start-btn">
              {migration?.status === "running" ? "Migrating…" : "Start migration"}
            </button>
            {migration?.status && (
              <div className="mt-3 text-xs" data-testid="migration-status">
                <p>Status: <b>{migration.status}</b> · migrated {migration.migrated ?? 0} · skipped {migration.skipped ?? 0} · failed {(migration.failed || []).length}</p>
                {migration.error && <p className="text-red-600 mt-1">{migration.error}</p>}
                {(migration.failed || []).map((f, i) => <p key={i} className="text-red-600 mt-0.5">{f.asset}: {f.error}</p>)}
                {migration.note && <p className="text-neutral-400 mt-1">{migration.note}</p>}
              </div>
            )}
          </div>
          <div className="bg-white border rounded p-4" data-testid="budget-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <b className="text-sm">Storage budget — Cloudflare R2</b>
              <button onClick={refreshBudget} className="text-xs border px-2 py-1 rounded" data-testid="budget-refresh">Refresh</button>
            </div>
            {!budget ? <p className="text-xs text-neutral-400 mt-2">Loading…</p> : (
              <>
                {budget.budget.alert && (
                  <p className={`mt-3 text-xs rounded border p-3 ${budget.budget.alert === "90" ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="budget-alert">
                    {budget.budget.pct_used}% of the ${budget.budget.usd}/month budget used — biggest consumer: {budget.budget.top_prefix || "—"}.
                  </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                  <div className="border rounded p-3"><p className="text-neutral-500">Stored</p><p className="text-lg font-semibold" data-testid="budget-total-gb">{budget.total_gb} GB</p><p className="text-neutral-400">{budget.total_objects} objects</p></div>
                  <div className="border rounded p-3"><p className="text-neutral-500">Est. this month</p><p className="text-lg font-semibold" data-testid="budget-cost">${budget.cost.total}</p><p className="text-neutral-400">projected ${budget.cost.projected_month_end}</p></div>
                  <div className="border rounded p-3"><p className="text-neutral-500">Class A writes</p><p className="text-lg font-semibold" data-testid="budget-class-a">{budget.ops.class_a.toLocaleString()}</p><p className="text-neutral-400">free {budget.ops.class_a_free.toLocaleString()}</p></div>
                  <div className="border rounded p-3"><p className="text-neutral-500">Class B reads</p><p className="text-lg font-semibold" data-testid="budget-class-b">{budget.ops.class_b.toLocaleString()}</p><p className="text-neutral-400">free {budget.ops.class_b_free.toLocaleString()}</p></div>
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-neutral-500 border-b"><th className="py-1">Prefix</th><th>Objects</th><th>GB</th></tr></thead>
                    <tbody>
                      {Object.entries(budget.prefixes).map(([k, v]) => (
                        <tr key={k} className="border-b border-neutral-100" data-testid={`prefix-${k}`}><td className="py-1 font-mono">{k}</td><td>{v.objects}</td><td>{v.gb}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <details className="mt-3">
                  <summary className="text-xs cursor-pointer text-brand" data-testid="rates-toggle">Cost rates (editable)</summary>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {[["storage_per_gb", "Storage $/GB-mo"], ["free_gb", "Free GB"], ["class_a_per_million", "Class A $/M"], ["class_a_free", "Class A free"], ["class_b_per_million", "Class B $/M"], ["class_b_free", "Class B free"], ["budget_usd", "Budget $/mo"]].map(([k, label]) => (
                      <label key={k} className="text-[11px] text-neutral-500">{label}
                        <input type="number" step="any" value={ratesDraft?.[k] ?? ""} onChange={(e) => setRatesDraft({ ...ratesDraft, [k]: Number(e.target.value) })}
                          className="mt-0.5 w-full border rounded px-2 py-1 text-xs" data-testid={`rate-${k}`} />
                      </label>
                    ))}
                  </div>
                  <button onClick={saveRates} className="mt-2 px-3 py-1.5 bg-neutral-900 text-white text-xs rounded" data-testid="rates-save">Save rates</button>
                </details>
                <details className="mt-3">
                  <summary className="text-xs cursor-pointer text-brand" data-testid="largest-toggle">Largest objects</summary>
                  <div className="mt-2 max-h-48 overflow-y-auto text-xs font-mono">
                    {budget.largest.map((o) => <p key={o.key} className="py-0.5 border-b border-neutral-50">{o.mb} MB — {o.key}</p>)}
                  </div>
                </details>
                <p className="text-[10px] text-neutral-400 mt-2">{budget.ops.note}. Backups count toward storage; retention unchanged (30 daily, 12 monthly).</p>
              </>
            )}
          </div>

          <div className="bg-white border rounded p-4" data-testid="orphan-panel">
            <b className="text-sm">Orphaned derivatives</b>
            <p className="text-xs text-neutral-500 mt-1">deriv/ files no media record points at. Review, select, purge — nothing deletes automatically.</p>
            {!orphans ? <p className="text-xs text-neutral-400 mt-2">Loading…</p> : (
              <>
                <p className="text-xs mt-2" data-testid="orphan-count">{orphans.count} orphans · {orphans.mb} MB</p>
                {orphans.count > 0 && (
                  <>
                    <div className="mt-2 max-h-40 overflow-y-auto text-xs font-mono">
                      {orphans.orphans.map((o) => (
                        <label key={o.key} className="flex items-center gap-2 py-0.5 border-b border-neutral-50">
                          <input type="checkbox" checked={!!purgeSel[o.key]} onChange={(e) => setPurgeSel({ ...purgeSel, [o.key]: e.target.checked })} data-testid={`orphan-${o.key.replace(/[^a-z0-9]/gi, "-")}`} />
                          <span>{o.key} ({(o.size / 1048576).toFixed(2)} MB)</span>
                        </label>
                      ))}
                    </div>
                    <button onClick={purgeOrphans} className="mt-2 px-3 py-1.5 bg-red-600 text-white text-xs rounded" data-testid="orphan-purge-btn">Purge selected</button>
                  </>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {focalAsset && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setFocalAsset(null)} data-testid="focal-modal">
          <div className="bg-white rounded-lg p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-medium">Focal point — click where crops must anchor</p>
            <p className="text-[11px] text-neutral-500 mb-3">{focalAsset.name} · every slot crops around this point via object-position.</p>
            <div className="relative cursor-crosshair bg-neutral-100" onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              saveFocal(focalAsset, ((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
            }}>
              <img src={focalAsset.url} alt="" className="w-full max-h-[50vh] object-contain mx-auto" data-testid="focal-image" />
              <span className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-brand bg-white/70 pointer-events-none"
                style={{ left: `${focalAsset.focal?.x ?? 50}%`, top: `${focalAsset.focal?.y ?? (focalAsset.height > focalAsset.width ? 33 : 50)}%` }}
                data-testid="focal-marker" />
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-3 mb-1">Crop check — every breakpoint</p>
            <div className="grid grid-cols-3 gap-2" data-testid="focal-crop-previews">
              {[["Wide slot · 21:9", "aspect-[21/9]"], ["Tall slot · 4:5", "aspect-[4/5]"], ["Tile · 1:1", "aspect-square"]].map(([label, cls]) => {
                const fx = focalAsset.focal?.x ?? 50;
                const fy = focalAsset.focal?.y ?? (focalAsset.height > focalAsset.width ? 33 : 50);
                return (
                  <div key={label}>
                    <div className={`${cls} overflow-hidden rounded border border-neutral-200 bg-neutral-50`}>
                      <img src={focalAsset.url} alt="" className="h-full w-full object-cover" style={{ objectPosition: `${fx}% ${fy}%` }} />
                    </div>
                    <p className="text-[10px] font-mono text-neutral-500 mt-1 text-center">{label}</p>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end mt-3">
              <button className="px-3 py-1.5 border rounded text-xs" onClick={() => setFocalAsset(null)} data-testid="focal-close-btn">Done</button>
            </div>
          </div>
        </div>
      )}
      {tab === "health" && (
        <section className="bg-white border rounded p-4 mb-3" data-testid="page-weight-panel">
          <b className="text-sm">Page weight check (PDP, mobile)</b>
          <p className="text-xs text-neutral-500 mt-1">
            Sums the 640w derivatives of every gallery image — video excluded by policy. Threshold:{" "}
            {pw?.threshold_kb ? `${(pw.threshold_kb / 1024).toFixed(1)} MB` : "2 MB"} (admin setting: page_weight_mobile_kb).
          </p>
          <div className="flex gap-2 mt-2">
            <input value={pwSlug} onChange={(e) => setPwSlug(e.target.value)} placeholder="slug (blank = sample of 8)"
              className="border rounded px-2 py-1.5 text-xs w-56" data-testid="pw-slug" />
            <button onClick={runPageWeight} disabled={pwBusy} className="px-3 py-1.5 bg-neutral-900 text-white text-xs rounded disabled:opacity-50" data-testid="pw-run">
              {pwBusy ? "Measuring…" : "Run check"}
            </button>
          </div>
          {pw && (
            <div className="mt-3 text-xs" data-testid="pw-results">
              {pw.rows.map((r) => (
                <p key={r.slug} className={`py-1 border-b border-neutral-50 ${r.over ? "text-red-600 font-semibold" : ""}`} data-testid={`pw-row-${r.slug}`}>
                  {r.title || r.slug} — {r.images} images · {r.mobile_kb} KB mobile {r.over ? "· OVER LIMIT" : ""}
                </p>
              ))}
              <p className="mt-1 text-neutral-500">{pw.over_count} over threshold</p>
            </div>
          )}
        </section>
      )}

      {tab === "health" && health && (
        <section className="grid md:grid-cols-3 gap-3 text-xs" data-testid="media-health">
          {[
            ["Storage (derivatives)", `${health.total_derivative_mb} MB across ${health.asset_count} assets`],
            ["Images without derivatives", health.images_without_derivatives],
            ["Products missing images", health.products_missing_images],
            ["Oversize assets", health.oversize_assets.length],
            ["Original-URL leaks on storefront", health.original_url_leaks, health.original_url_leaks > 0],
          ].map(([label, val, alert]) => (
            <div key={label} className={`bg-white border rounded p-4 ${alert ? "border-red-400" : ""}`}>
              <div className="text-neutral-500">{label}</div>
              <div className={`text-lg font-semibold ${alert ? "text-red-600" : ""}`}>{val}</div>
            </div>
          ))}
          <div className="bg-white border rounded p-4 md:col-span-3">
            <b>Largest assets</b>
            {health.largest.map((a) => <div key={a.name} className="flex justify-between py-0.5"><span className="truncate">{a.name}</span><span>{a.mb} MB</span></div>)}
            {!health.largest.length && <div className="text-neutral-400 py-2">No assets yet.</div>}
          </div>
        </section>
      )}
    </div>
  );

/* ---------- Unmatched ingest (e.g. collection-01 drop) ----------
 * Grid of unassigned images; type a design number + Enter to assign.
 * Arrow keys move between images. A two-column CSV (filename, design number)
 * assigns the whole batch in one pass. Nothing is auto-guessed. */
function UnmatchedTab({ onDone }) {
  const [rows, setRows] = useState([]);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState("");
  const inputRefs = useRef({});

  const load = () => api.get("/admin/media/unmatched").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const assign = async (assetId, idx) => {
    const dn = (vals[assetId] || "").trim();
    if (!dn) return;
    setBusy(assetId);
    try {
      const r = await api.post("/admin/media/assign", { asset_id: assetId, design_number: dn });
      toast.success(`${r.data.product} — added to ${r.data.colour} set. Run the recolour batch to derive yellow/white.`);
      setRows((rs) => rs.filter((x) => (x.id || x._id) !== assetId));
      onDone?.();
      const next = rows[idx + 1];
      if (next) inputRefs.current[next.id || next._id]?.focus();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const uploadMap = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/media/assign-map", fd);
      toast.success(`${r.data.assigned} assigned${r.data.missing.length ? `, ${r.data.missing.length} not matched` : ""}`);
      if (r.data.missing.length) toast.warning(`Unmatched: ${r.data.missing.slice(0, 5).map((m) => m.filename).join(", ")}${r.data.missing.length > 5 ? "…" : ""}`);
      load();
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="bg-white border border-neutral-200 p-5" data-testid="unmatched-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1">
          <h2 className="font-heading font-semibold">Unmatched images ({rows.length})</h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            Type the design number and press Enter. ↑/↓ moves between images. For the collection-01 mapping file:
            upload a two-column CSV (filename, design number) to assign everything in one pass.
          </p>
        </div>
        <label className="text-xs border border-neutral-300 px-3 py-2 cursor-pointer hover:border-stone-500" data-testid="unmatched-csv-label">
          Upload mapping CSV
          <input type="file" accept=".csv" className="hidden" data-testid="unmatched-csv-input"
            onChange={(e) => { uploadMap(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
      {!rows.length && <p className="text-sm text-neutral-400" data-testid="unmatched-empty">Nothing unmatched — every image is assigned.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        {rows.map((a, i) => (
          <div key={a.id || a._id} className="border border-neutral-200" data-testid={`unmatched-${a.name}`}>
            <img src={a.srcset?.["640"]?.webp || a.url} alt={a.name} className="w-full aspect-square object-cover" loading="lazy" />
            <p className="text-[10px] font-mono text-neutral-500 px-2 pt-1.5 truncate">{a.name}</p>
            {a.tags?.source && <p className="text-[10px] font-mono text-brand px-2">{a.tags.source}{a.tags.colour ? ` · ${a.tags.colour}` : ""}</p>}
            <input
              ref={(el) => { inputRefs.current[a.id || a._id] = el; }}
              value={vals[a.id || a._id] || ""}
              onChange={(e) => setVals((v) => ({ ...v, [a.id || a._id]: e.target.value.toUpperCase() }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") assign(a.id || a._id, i);
                if (e.key === "ArrowDown" || e.key === "ArrowRight") inputRefs.current[rows[i + 1]?.id || rows[i + 1]?._id]?.focus();
                if (e.key === "ArrowUp" || e.key === "ArrowLeft") inputRefs.current[rows[i - 1]?.id || rows[i - 1]?._id]?.focus();
              }}
              placeholder="Design no. + Enter"
              disabled={busy === (a.id || a._id)}
              className="m-2 mt-1.5 w-[calc(100%-1rem)] h-8 border border-neutral-300 rounded px-2 text-xs font-mono focus:outline-none focus:border-stone-900"
              data-testid={`unmatched-input-${a.name}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

}
